// Landing page for AI Bot — medical clinic AI communication platform
// Ukrainian copy, clinical & calm aesthetic

import React from "react";
import { createRoot } from "react-dom/client";
import {
  useTweaks,
  TweaksPanel,
  TweakSection,
  TweakRadio,
} from "./tweaks-panel.jsx";
import { t as tr, tList } from "./i18n/index.js";

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "teal"
}/*EDITMODE-END*/;

const BRAND_ASSET_URLS =
  typeof window !== 'undefined' && window.__brandAssetUrls && typeof window.__brandAssetUrls === 'object'
    ? window.__brandAssetUrls
    : { horizontal: '/brand/ai-bot-horizontal.svg', icon: '/brand/ai-bot-icon.svg' };

const SUPPORTED_LANGUAGES = ['uk', 'en'];

function currentLanguage() {
  if (typeof window === 'undefined') return 'uk';
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
  return SUPPORTED_LANGUAGES.includes(firstSegment) ? firstSegment : 'uk';
}

function stripLanguagePrefix(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
    segments.shift();
  }
  return segments.length === 0 ? '/' : `/${segments.join('/')}`;
}

function localizedPath(language, targetPath, includeSearch = false) {
  const safeLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : 'uk';
  const basePath = targetPath ?? stripLanguagePrefix(window.location.pathname);
  const normalizedPath = basePath.startsWith('/') ? basePath : `/${basePath}`;
  const suffix = normalizedPath === '/' ? '' : normalizedPath;
  const search = includeSearch && typeof window !== 'undefined' ? window.location.search : '';
  const hash = includeSearch && typeof window !== 'undefined' ? window.location.hash : '';
  return `/${safeLanguage}${suffix}${search}${hash}`;
}

// ── Theme palettes ──────────────────────────────────────────────────────────
const THEMES = {
  teal: {
    bg:       'oklch(0.993 0.004 180)',
    surface:  'oklch(0.965 0.015 170)',
    surface2: 'oklch(0.98 0.01 175)',
    fg:       'oklch(0.22 0.012 220)',
    muted:    'oklch(0.48 0.012 215)',
    subtle:   'oklch(0.62 0.01 215)',
    border:   'oklch(0.915 0.012 200)',
    borderSoft: 'oklch(0.945 0.008 200)',
    accent:   'oklch(0.58 0.065 185)',
    accentSoft: 'oklch(0.93 0.03 180)',
    accentFg: 'oklch(0.32 0.06 190)',
  },
  navy: {
    bg:       'oklch(0.992 0.004 255)',
    surface:  'oklch(0.96 0.012 255)',
    surface2: 'oklch(0.978 0.008 255)',
    fg:       'oklch(0.21 0.03 260)',
    muted:    'oklch(0.46 0.02 260)',
    subtle:   'oklch(0.60 0.015 260)',
    border:   'oklch(0.91 0.012 255)',
    borderSoft: 'oklch(0.94 0.008 255)',
    accent:   'oklch(0.42 0.11 255)',
    accentSoft: 'oklch(0.93 0.03 255)',
    accentFg: 'oklch(0.30 0.1 255)',
  },
  warm: {
    bg:       'oklch(0.99 0.008 70)',
    surface:  'oklch(0.955 0.016 65)',
    surface2: 'oklch(0.975 0.012 65)',
    fg:       'oklch(0.23 0.015 50)',
    muted:    'oklch(0.48 0.02 55)',
    subtle:   'oklch(0.62 0.015 55)',
    border:   'oklch(0.91 0.014 60)',
    borderSoft: 'oklch(0.94 0.01 60)',
    accent:   'oklch(0.55 0.085 45)',
    accentSoft: 'oklch(0.94 0.03 55)',
    accentFg: 'oklch(0.32 0.08 45)',
  },
  mono: {
    bg:       'oklch(0.995 0 0)',
    surface:  'oklch(0.965 0 0)',
    surface2: 'oklch(0.98 0 0)',
    fg:       'oklch(0.2 0 0)',
    muted:    'oklch(0.48 0 0)',
    subtle:   'oklch(0.62 0 0)',
    border:   'oklch(0.91 0 0)',
    borderSoft: 'oklch(0.94 0 0)',
    accent:   'oklch(0.28 0 0)',
    accentSoft: 'oklch(0.93 0 0)',
    accentFg: 'oklch(0.2 0 0)',
  },
};

// ── Utilities ───────────────────────────────────────────────────────────────
function useIsInView(ref, threshold = 0.2) {
  const [inView, setInView] = React.useState(false);
  React.useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return inView;
}

// ── Icons (minimal line glyphs) ─────────────────────────────────────────────
const I = {
  spark: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/></svg>,
  handoff: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="7" cy="8" r="3"/><circle cx="17" cy="8" r="3"/><path d="M2 20c0-3 2.5-5 5-5s5 2 5 5M12 20c0-3 2.5-5 5-5s5 2 5 5"/><path d="M10 11l4 0"/></svg>,
  shield: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z"/><path d="M9 12l2 2 4-4"/></svg>,
  chat: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 5h16v11H8l-4 4V5z"/></svg>,
  clock: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  plug: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M9 2v6M15 2v6M7 8h10v4a5 5 0 01-10 0V8zM12 17v5"/></svg>,
  book: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 4h10a4 4 0 014 4v12H8a4 4 0 01-4-4V4z"/><path d="M4 4v12a4 4 0 014-4h10"/></svg>,
  users: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2.5"/><path d="M3 20c0-3 2.5-5 6-5s6 2 6 5M15 20c0-2.5 2-4 4.5-4"/></svg>,
  building: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 20V6l8-3 8 3v14M4 20h16M9 10h.01M15 10h.01M9 14h.01M15 14h.01M10 20v-4h4v4"/></svg>,
  flask: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M9 3h6M10 3v6l-5 9a2 2 0 001.8 3h10.4A2 2 0 0019 18l-5-9V3"/><path d="M7.5 14h9"/></svg>,
  heart: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 20s-7-4.5-7-10a4 4 0 017-2.6A4 4 0 0119 10c0 5.5-7 10-7 10z"/></svg>,
  check: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M5 12l5 5 9-11"/></svg>,
  arrow: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M5 12h14M13 6l6 6-6 6"/></svg>,
  plus: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" {...p}><path d="M12 5v14M5 12h14"/></svg>,
  minus: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" {...p}><path d="M5 12h14"/></svg>,
  close: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" {...p}><path d="M6 6l12 12M18 6L6 18"/></svg>,
  send: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 12l16-8-6 17-3-7-7-2z"/></svg>,
};

// ── Channel logos (monochrome glyphs, placeholder-ish) ──────────────────────
const Ch = {
  telegram: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M21.9 4.3 2.7 11.6c-1 .4-1 1.7 0 2l4.9 1.6 2 6.3c.2.7 1 .9 1.5.4l2.8-2.5 4.9 3.6c.9.7 2.1.2 2.3-.9l3-15c.2-1.1-.9-2.1-2.2-1.7zM9.4 15l-.7 4.6-1.5-5 9.2-6-7 6.4z"/></svg>,
  facebook: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M14 22v-8h2.7l.4-3H14V8.8c0-.9.3-1.5 1.6-1.5H17V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4v2.5H8V14h2.6v8H14z"/></svg>,
  instagram: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...p}><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>,
  web: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...p}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></svg>,
  helpcrunch: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2h-7l-4 3v-3H6a2 2 0 01-2-2V6z"/><path d="M9 10h.01M12 10h.01M15 10h.01"/></svg>,
  clinica: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 9v6M9 12h6"/><path d="M3 8h18"/></svg>,
};

// ════════════════════════════════════════════════════════════════════════════
// HERO — Abstract AI + human handover diagram + live chat demo
// ════════════════════════════════════════════════════════════════════════════
function Hero({ t }) {
  const lang = currentLanguage();

  return (
    <section className="hero">
      <div className="hero-inner">
        <div className="hero-left">
          <div className="eyebrow">
            <span className="dot" /> {tr("brand.eyebrow")}
          </div>
          <h1 className="h1">
            <span className="h1-accent">{tr("hero.titleAccent")}</span><br/>
            {tr("hero.titleRest")}
          </h1>
          <p className="lede">{tr("hero.lede")}</p>
          <div className="cta-row">
            <a className="btn btn-primary" href={localizedPath(lang, '/Register')}>
              {tr("hero.ctaTry")} <I.arrow width="16" height="16"/>
            </a>
            <a className="btn btn-ghost" href={localizedPath(lang, '/Auth/Login')}>
              {tr("hero.ctaLogin")}
            </a>
          </div>
          <div className="hero-meta">
            <div><strong>24/7</strong><span>{tr("hero.metric247Label")}</span></div>
            <div className="divider"/>
            <div><strong>{tr("hero.metricLoadValue")}</strong><span>{tr("hero.metricLoadLabel")}</span></div>
            <div className="divider"/>
            <div><strong>{tr("hero.metricChannelsValue")}</strong><span>{tr("hero.metricChannelsLabel")}</span></div>
          </div>
        </div>
        <div className="hero-right">
          <HeroDiagram t={t} />
        </div>
      </div>
    </section>
  );
}

// Abstract AI + human handover diagram with live chat demo overlay
function HeroDiagram({ t }) {
  const [step, setStep] = React.useState(0); // 0: patient → AI, 1: AI thinking, 2: AI reply, 3: handoff, 4: operator
  const messages = [
    { from: 'patient', text: tr('heroChat.msg1') },
    { from: 'ai', text: tr('heroChat.msg2') },
    { from: 'patient', text: tr('heroChat.msg3') },
    { from: 'ai', text: tr('heroChat.msg4'), kind: 'system' },
    { from: 'operator', text: tr('heroChat.msg5') },
  ];

  React.useEffect(() => {
    const id = setInterval(() => {
      setStep((s) => (s + 1) % 6);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  const visible = messages.slice(0, Math.min(step, messages.length));

  return (
    <div className="diagram">
      {/* Nodes */}
      <div className="node node-patient">
        <div className="node-avatar">{tr('heroChat.nodePatientName').charAt(0)}</div>
        <div className="node-label">
          <span>{tr('heroChat.nodePatient')}</span>
          <em>{tr('heroChat.nodePatientName')}</em>
        </div>
      </div>

      <div className="node node-ai">
        <div className="node-avatar ai">
          <I.spark width="18" height="18"/>
        </div>
        <div className="node-label">
          <span>{tr('heroChat.nodeAi')}</span>
          <em>{tr('heroChat.nodeAiRole')}</em>
        </div>
      </div>

      <div className="node node-operator">
        <div className="node-avatar op">{tr('heroChat.nodeOperatorName').charAt(0)}</div>
        <div className="node-label">
          <span>{tr('heroChat.nodeOperator')}</span>
          <em>{tr('heroChat.nodeOperatorName')}</em>
        </div>
      </div>

      {/* SVG connection lines */}
      <svg className="diagram-lines" viewBox="0 0 400 320" preserveAspectRatio="none">
        <defs>
          <linearGradient id="line1" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="currentColor" stopOpacity="0.2"/>
            <stop offset="1" stopColor="currentColor" stopOpacity="0.5"/>
          </linearGradient>
        </defs>
        <path d="M 60 60 C 140 60, 140 160, 200 160" stroke="currentColor" strokeWidth="1" fill="none" strokeOpacity=".2" strokeDasharray="3 3"/>
        <path d="M 200 160 C 260 160, 260 260, 340 260" stroke="currentColor" strokeWidth="1" fill="none" strokeOpacity=".2" strokeDasharray="3 3"/>
        {/* Moving dot along the active path */}
        <circle r="3" fill={t.accent}>
          <animateMotion dur="2.4s" repeatCount="indefinite"
            path={step < 3 ? "M 60 60 C 140 60, 140 160, 200 160" : "M 200 160 C 260 160, 260 260, 340 260"} />
        </circle>
      </svg>

      {/* Chat card */}
      <div className="chat-card">
        <div className="chat-head">
          <div className="chat-head-l">
            <Ch.telegram width="14" height="14" style={{color: t.accent}}/>
            <span>{tr('heroChat.channelTitle')}</span>
          </div>
          <span className="chat-live">●&nbsp;LIVE</span>
        </div>
        <div className="chat-body">
          {visible.map((m, i) => (
            <ChatBubble key={i} m={m} />
          ))}
          {step >= messages.length && step < 6 && (
            <div className="chat-typing">
              <span/><span/><span/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ m }) {
  if (m.kind === 'system') {
    return (
      <div className="msg msg-sys">
        <span className="sys-dot"/>
        <span>{m.text}</span>
      </div>
    );
  }
  const side = m.from === 'patient' ? 'l' : 'r';
  const role = m.from === 'ai' ? tr('heroChat.roleAi') : m.from === 'operator' ? tr('heroChat.roleOperator') : tr('heroChat.rolePatient');
  return (
    <div className={`msg msg-${side}`}>
      <div className="msg-role">{role}</div>
      <div className={`bubble bubble-${m.from}`}>{m.text}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SAFETY BAND — "No diagnoses" disclaimer
// ════════════════════════════════════════════════════════════════════════════
function SafetyBand() {
  return (
    <section className="safety-band">
      <div className="safety-inner">
        <I.shield width="18" height="18"/>
        <p>
          <strong>{tr("safety.strong")}</strong>
          &nbsp;{tr("safety.text")}
        </p>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// BENEFITS — 6 blocks
// ════════════════════════════════════════════════════════════════════════════
const BENEFIT_ICONS = ['clock', 'chat', 'handoff', 'plug', 'book', 'shield'];

function Benefits() {
  const items = tList("benefits.items");
  return (
    <section id="benefits" className="section">
      <SectionHeader
        eyebrow={tr("benefits.eyebrow")}
        title={<>{tr("benefits.titleAccent")}<br/><span className="muted">{tr("benefits.titleMuted")}</span></>}
      />
      <div className="grid grid-3">
        {items.map((b, i) => {
          const Icon = I[BENEFIT_ICONS[i]] || I.spark;
          return (
            <div key={i} className="card card-benefit">
              <div className="card-icon"><Icon width="22" height="22"/></div>
              <h3 className="card-title">{b.title}</h3>
              <p className="card-body">{b.body}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// HOW IT WORKS — Interactive stepper
// ════════════════════════════════════════════════════════════════════════════
const STEP_VISUALS = ['channels', 'ai', 'handoff', 'sync'];
const STEP_NUMBERS = ['01', '02', '03', '04'];

function HowItWorks({ t }) {
  const steps = tList("how.steps");
  const [active, setActive] = React.useState(0);
  const [auto, setAuto] = React.useState(true);
  React.useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => setActive((a) => (a + 1) % steps.length), 4200);
    return () => clearInterval(id);
  }, [auto, steps.length]);

  return (
    <section id="how" className="section how">
      <SectionHeader
        eyebrow={tr("how.eyebrow")}
        title={<>{tr("how.titleAccent")}<br/><span className="muted">{tr("how.titleMuted")}</span></>}
      />
      <div className="how-grid">
        <div className="how-steps">
          {steps.map((s, i) => (
            <button
              key={i}
              className={`step ${i === active ? 'is-active' : ''}`}
              onClick={() => { setActive(i); setAuto(false); }}
            >
              <span className="step-n">{STEP_NUMBERS[i]}</span>
              <div className="step-body">
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
              <span className="step-bar"/>
            </button>
          ))}
        </div>
        <div className="how-visual">
          <StepVisual kind={STEP_VISUALS[active]} t={t}/>
        </div>
      </div>
    </section>
  );
}

const CHANNELS_ROW_META = [
  { ch: 'telegram', n: 1 },
  { ch: 'instagram', n: 2 },
  { ch: 'web' },
  { ch: 'facebook' },
];

function StepVisual({ kind, t }) {
  if (kind === 'channels') {
    const rows = tList("how.visual.inboxRows");
    return (
      <div className="sv sv-channels">
        <div className="sv-title">{tr("how.visual.channelsTitle")}</div>
        <div className="sv-inbox">
          {rows.map((row, i) => {
            const meta = CHANNELS_ROW_META[i] || { ch: 'web' };
            const Logo = Ch[meta.ch];
            return (
              <div key={i} className="inbox-row">
                <div className="inbox-ch" style={{color: t.accent}}><Logo width="14" height="14"/></div>
                <div className="inbox-meta">
                  <div className="inbox-name">{row.name}</div>
                  <div className="inbox-prev">{row.preview}</div>
                </div>
                <div className="inbox-t">
                  {meta.n && <span className="inbox-badge">{meta.n}</span>}
                  <span>{row.t}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  if (kind === 'ai') {
    return (
      <div className="sv sv-ai">
        <div className="sv-title">{tr("how.visual.aiTitle")}</div>
        <div className="ai-bubble">
          <I.spark width="16" height="16" style={{color: t.accent}}/>
          <div>
            <strong>{tr("how.visual.aiQueryLabel")}</strong> {tr("how.visual.aiQuery")}
          </div>
        </div>
        <div className="ai-sources">
          <div className="ai-src"><span className="pin"/>{tr("how.visual.aiSrc1")}</div>
          <div className="ai-src"><span className="pin"/>{tr("how.visual.aiSrc2")}</div>
          <div className="ai-src"><span className="pin"/>{tr("how.visual.aiSrc3")}</div>
        </div>
        <div className="ai-bubble ai-bubble-out">
          <div>
            {tr("how.visual.aiAnswer1")} <strong>650 ₴</strong>{tr("how.visual.aiAnswer2")}
          </div>
        </div>
      </div>
    );
  }
  if (kind === 'handoff') {
    const points = tList("how.visual.handoffPoints");
    return (
      <div className="sv sv-handoff">
        <div className="sv-title">{tr("how.visual.handoffTitle")}</div>
        <div className="ho-card">
          <div className="ho-head">
            <div className="ho-avatar">{tr("how.visual.handoffName").charAt(0)}</div>
            <div>
              <strong>{tr("how.visual.handoffName")}</strong>
              <em>{tr("how.visual.handoffDialog")}</em>
            </div>
            <span className="ho-tag">{tr("how.visual.handoffTag")}</span>
          </div>
          <div className="ho-summary">
            <div className="ho-s-label">{tr("how.visual.handoffSummary")}</div>
            <ul>
              {points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
          <div className="ho-assign">
            <span>{tr("how.visual.handoffAssigned")} <strong>{tr("how.visual.handoffAssignee")}</strong></span>
            <span className="ho-time">{tr("how.visual.handoffTime")}</span>
          </div>
        </div>
      </div>
    );
  }
  if (kind === 'sync') {
    return (
      <div className="sv sv-sync">
        <div className="sv-title">{tr("how.visual.syncTitle")}</div>
        <div className="sync-diagram">
          <div className="sync-side">
            <div className="sync-logo"><I.spark width="16" height="16"/></div>
            <strong>{tr("how.visual.syncAiName")}</strong>
            <em>{tr("how.visual.syncAiRole")}</em>
          </div>
          <div className="sync-pipe">
            <div className="pipe-row"><span>→</span> {tr("how.visual.syncRow1")}</div>
            <div className="pipe-row"><span>→</span> {tr("how.visual.syncRow2")}</div>
            <div className="pipe-row"><span>←</span> {tr("how.visual.syncRow3")}</div>
            <div className="pipe-row"><span>←</span> {tr("how.visual.syncRow4")}</div>
          </div>
          <div className="sync-side">
            <div className="sync-logo"><Ch.clinica width="16" height="16"/></div>
            <strong>{tr("how.visual.syncCwName")}</strong>
            <em>{tr("how.visual.syncCwRole")}</em>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════════════
// FEATURES — Detailed blocks
// ════════════════════════════════════════════════════════════════════════════
function FeatureBlock({ keyBase, visual, reverse }) {
  const list = tList(`${keyBase}.list`);
  return (
    <div className={`feature ${reverse ? 'feature-reverse' : 'feature-lg'}`}>
      <div className="feature-text">
        <span className="feature-tag">{tr(`${keyBase}.tag`)}</span>
        <h3>{tr(`${keyBase}.title`)}</h3>
        <p>{tr(`${keyBase}.body`)}</p>
        <ul className="feat-list">
          {list.map((item, i) => (
            <li key={i}><I.check width="16" height="16"/> {item}</li>
          ))}
        </ul>
      </div>
      <div className="feature-visual">{visual}</div>
    </div>
  );
}

function Features({ t }) {
  return (
    <section id="features" className="section">
      <SectionHeader
        eyebrow={tr("features.eyebrow")}
        title={<>{tr("features.titleAccent")}<br/><span className="muted">{tr("features.titleMuted")}</span></>}
      />
      <FeatureBlock keyBase="features.f1" visual={<InboxMock t={t}/>} />
      <FeatureBlock keyBase="features.f2" visual={<KnowledgeMock t={t}/>} reverse />
      <FeatureBlock keyBase="features.f3" visual={<AdminMock t={t}/>} />
    </section>
  );
}

function InboxMock({ t }) {
  const names = tList("features.inboxMock.names");
  return (
    <div className="mock mock-inbox">
      <div className="mock-bar">
        <span className="mock-dot" style={{background: '#ff5f57'}}/>
        <span className="mock-dot" style={{background: '#febc2e'}}/>
        <span className="mock-dot" style={{background: '#28c840'}}/>
        <span className="mock-title">{tr("features.inboxMock.title")}</span>
      </div>
      <div className="mock-body">
        <aside className="mock-side">
          <div className="mock-side-h">{tr("features.inboxMock.sideHeader")}</div>
          {names.map((n, i) => (
            <div key={i} className={`mock-side-row ${i === 0 ? 'is-on' : ''}`}>
              <div className="mock-av">{n[0]}</div>
              <div>
                <div className="mock-n">{n}</div>
                <div className="mock-p">{tr("features.inboxMock.lastSeen")}</div>
              </div>
              {i === 0 && <span className="mock-badge">3</span>}
            </div>
          ))}
        </aside>
        <div className="mock-main">
          <div className="mock-h">
            <div>
              <strong>{names[0]}</strong>
              <em>{tr("features.inboxMock.headerSub")}</em>
            </div>
            <div className="mock-tags">
              <span className="tag tag-accent">{tr("features.inboxMock.tag1")}</span>
              <span className="tag">{tr("features.inboxMock.tag2")}</span>
            </div>
          </div>
          <div className="mock-chat">
            <div className="mock-msg in">{tr("features.inboxMock.msg1")}</div>
            <div className="mock-msg out ai">
              <span className="ai-mark" style={{color: t.accent}}><I.spark width="11" height="11"/></span>
              {tr("features.inboxMock.msg2")}
            </div>
            <div className="mock-msg in">{tr("features.inboxMock.msg3")}</div>
            <div className="mock-sys">{tr("features.inboxMock.sysHandoff")}</div>
            <div className="mock-msg out">{tr("features.inboxMock.msg4")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KnowledgeMock({ t }) {
  const sources = tList("features.kbMock.sources");
  return (
    <div className="mock mock-kb">
      <div className="mock-bar">
        <span className="mock-dot" style={{background: '#ff5f57'}}/>
        <span className="mock-dot" style={{background: '#febc2e'}}/>
        <span className="mock-dot" style={{background: '#28c840'}}/>
        <span className="mock-title">{tr("features.kbMock.title")}</span>
      </div>
      <div className="kb-body">
        <div className="kb-head">
          <div>
            <h4>{tr("features.kbMock.head")}</h4>
            <span>{tr("features.kbMock.sub")}</span>
          </div>
          <button className="kb-add"><I.plus width="14" height="14"/> {tr("features.kbMock.add")}</button>
        </div>
        {sources.map((s, i) => (
          <div key={i} className="kb-row">
            <span className="kb-type">{s.type}</span>
            <div className="kb-title">{s.title}</div>
            <span className="kb-upd">{s.updated}</span>
            <span className="kb-on" style={{background: t.accentSoft, color: t.accentFg}}>on</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ADMIN_CHANNELS = ['web', 'telegram', 'facebook', 'instagram'];

function AdminMock({ t }) {
  const channels = tList("features.adminMock.channels");
  const connected = tr("features.adminMock.connected");
  return (
    <div className="mock mock-admin">
      <div className="mock-bar">
        <span className="mock-dot" style={{background: '#ff5f57'}}/>
        <span className="mock-dot" style={{background: '#febc2e'}}/>
        <span className="mock-dot" style={{background: '#28c840'}}/>
        <span className="mock-title">{tr("features.adminMock.title")}</span>
      </div>
      <div className="adm-body">
        <div className="adm-stats">
          <div className="adm-stat">
            <span className="adm-k">{tr("features.adminMock.stat1")}</span>
            <strong>1 284</strong>
            <span className="adm-d" style={{color: t.accentFg}}>+18%</span>
          </div>
          <div className="adm-stat">
            <span className="adm-k">{tr("features.adminMock.stat2")}</span>
            <strong>71%</strong>
            <span className="adm-d" style={{color: t.accentFg}}>+6%</span>
          </div>
          <div className="adm-stat">
            <span className="adm-k">{tr("features.adminMock.stat3")}</span>
            <strong>{tr("features.adminMock.stat3Value")}</strong>
            <span className="adm-d">AI</span>
          </div>
        </div>
        <div className="adm-section">
          <div className="adm-sh">{tr("features.adminMock.section")}</div>
          {channels.map((r, i) => {
            const Logo = Ch[ADMIN_CHANNELS[i]];
            return (
              <div key={i} className="adm-row">
                <Logo width="14" height="14" style={{color: t.accent}}/>
                <div className="adm-row-n">{r.name}</div>
                <span className="adm-row-st"><span className="dot-on"/>{connected}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// INTEGRATIONS — Logo grid
// ════════════════════════════════════════════════════════════════════════════
const INTEGRATION_META = [
  { ch: 'clinica', primary: true },
  { ch: 'telegram' },
  { ch: 'facebook' },
  { ch: 'instagram' },
  { ch: 'web' },
  { ch: 'helpcrunch' },
];

function Integrations({ t }) {
  const items = tList("integrations.items");
  return (
    <section id="integrations" className="section section-surface">
      <SectionHeader
        eyebrow={tr("integrations.eyebrow")}
        title={<>{tr("integrations.titleAccent")}<br/><span className="muted">{tr("integrations.titleMuted")}</span></>}
      />
      <div className="int-grid">
        {items.map((it, i) => {
          const meta = INTEGRATION_META[i] || { ch: 'web' };
          const Logo = Ch[meta.ch];
          return (
            <div key={i} className={`int-card ${meta.primary ? 'int-primary' : ''}`}>
              <div className="int-logo" style={{color: meta.primary ? t.accent : t.fg}}>
                <Logo width="26" height="26"/>
              </div>
              <div className="int-name">{it.name}</div>
              <div className="int-desc">{it.desc}</div>
              {meta.primary && <span className="int-badge" style={{background: t.accentSoft, color: t.accentFg}}>{tr("integrations.primaryBadge")}</span>}
            </div>
          );
        })}
      </div>
      <div className="int-note">
        {tr("integrations.note1")} <a href="mailto:hello@aibot.clinic">{tr("integrations.noteLink")}</a> {tr("integrations.note2")}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// USE CASES
// ════════════════════════════════════════════════════════════════════════════
const CASE_ICONS = ['building', 'flask', 'heart'];

function UseCases({ t }) {
  const cases = tList("cases.items");
  const photoLabel = tr("cases.photoLabel");
  return (
    <section id="cases" className="section">
      <SectionHeader
        eyebrow={tr("cases.eyebrow")}
        title={<>{tr("cases.titleAccent")}<br/><span className="muted">{tr("cases.titleMuted")}</span></>}
      />
      <p className="cases-sub">{tr("cases.sub")}</p>
      <div className="cases-grid">
        {cases.map((c, i) => {
          const Icon = I[CASE_ICONS[i]] || I.spark;
          return (
            <div key={i} className="case">
              <div className="case-head">
                <div className="case-icon" style={{color: t.accent, background: t.accentSoft}}>
                  <Icon width="18" height="18"/>
                </div>
                <span className="case-tag">{c.tag}</span>
              </div>
              <h3 className="case-title">{c.title}</h3>
              <p className="case-body">{c.body}</p>
              <div className="case-metric">
                <strong>{c.metric}</strong>
              </div>
              <div className="case-photo">
                <div className="placeholder-ph">
                  <span>{photoLabel} · {c.tag.toLowerCase()}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FAQ
// ════════════════════════════════════════════════════════════════════════════
function FAQ() {
  const items = tList("faq.items");
  const [open, setOpen] = React.useState(0);
  return (
    <section id="faq" className="section">
      <SectionHeader
        eyebrow={tr("faq.eyebrow")}
        title={<>{tr("faq.titleAccent")}<br/><span className="muted">{tr("faq.titleMuted")}</span></>}
      />
      <div className="faq-list">
        {items.map((f, i) => (
          <button
            key={i}
            className={`faq-row ${open === i ? 'is-open' : ''}`}
            onClick={() => setOpen(open === i ? -1 : i)}
          >
            <div className="faq-q">
              <span>{f.q}</span>
              <span className="faq-toggle">
                {open === i ? <I.minus width="18" height="18"/> : <I.plus width="18" height="18"/>}
              </span>
            </div>
            <div className="faq-a-wrap">
              <div className="faq-a">{f.a}</div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CTA
// ════════════════════════════════════════════════════════════════════════════
function CTA({ t }) {
  const lang = currentLanguage();

  return (
    <section id="get-started" className="section cta">
      <div className="cta-card cta-card-simple">
        <div className="cta-copy">
          <span className="eyebrow eyebrow-accent"><span className="dot"/> {tr("cta.eyebrow")}</span>
          <h2 className="cta-h">
            {tr("cta.title1")}<br/>{tr("cta.title2")}
          </h2>
          <p className="cta-sub">{tr("cta.sub")}</p>
          <ul className="cta-list">
            {tList("cta.list").map((item, i) => (
              <li key={i}><I.check width="16" height="16"/> {item}</li>
            ))}
          </ul>
          <div className="cta-actions">
            <a href={localizedPath(lang, '/Register')} className="btn btn-primary">
              {tr("cta.btnTry")} <I.arrow width="16" height="16"/>
            </a>
            <a href={localizedPath(lang, '/Demo')} className="btn btn-ghost">{tr("cta.btnDemo")}</a>
          </div>
        </div>
        <div className="cta-visual">
          <div className="cta-device">
            <div className="cta-device-h">
              <span className="mock-dot" style={{background:'#ff5f57'}}/>
              <span className="mock-dot" style={{background:'#febc2e'}}/>
              <span className="mock-dot" style={{background:'#28c840'}}/>
              <span className="cta-device-url">app.aibot.clinic</span>
            </div>
            <div className="cta-device-body">
              <div className="cta-dev-logo"><img src={BRAND_ASSET_URLS.icon} alt="" className="cta-dev-logo-img" width="40" height="40" /></div>
              <div className="cta-dev-t">{tr("cta.deviceTitle")}</div>
              <div className="cta-dev-s">{tr("cta.deviceSub")}</div>
              <div className="cta-dev-box">
                <div className="cta-dev-cap">{tr("cta.deviceCap")}</div>
                <div className="cta-dev-btn">
                  <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.3 1.5 8.2 2.8l6-5.8C34.6 3 29.7 1 24 1 14.8 1 7 6.3 3.3 14l7.1 5.5C12.2 13.7 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.6c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7c4.3-4 6.8-9.9 6.8-17.2z"/><path fill="#FBBC05" d="M10.4 28.5c-.5-1.5-.8-3.1-.8-4.8s.3-3.3.8-4.8l-7.1-5.5C1.6 16.6.5 20.2.5 24s1.1 7.4 3 10.6l6.9-6.1z"/><path fill="#34A853" d="M24 47c6.5 0 11.9-2.1 15.8-5.7l-7.4-5.7c-2 1.4-4.7 2.3-8.4 2.3-6.4 0-11.8-4.3-13.7-10.1l-7 5.4C7 41.7 14.8 47 24 47z"/></svg>
                  {tr("cta.google")}
                </div>
                <div className="cta-dev-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24"><rect x="1" y="1" width="10" height="10" fill="#F25022"/><rect x="13" y="1" width="10" height="10" fill="#7FBA00"/><rect x="1" y="13" width="10" height="10" fill="#00A4EF"/><rect x="13" y="13" width="10" height="10" fill="#FFB900"/></svg>
                  {tr("cta.microsoft")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// HEADER + FOOTER + SECTION HEADER
// ════════════════════════════════════════════════════════════════════════════
function SectionHeader({ eyebrow, title }) {
  return (
    <div className="sect-h">
      <span className="eyebrow"><span className="dot"/> {eyebrow}</span>
      <h2 className="h2">{title}</h2>
    </div>
  );
}

function Header() {
  const lang = currentLanguage();

  return (
    <header className="nav">
      <div className="nav-inner">
        <a href={localizedPath(lang, '/')} className="logo">
          <img src={BRAND_ASSET_URLS.horizontal} alt="AI Bot" className="logo-img" />
        </a>
        <nav className="nav-links">
          <a href="#benefits">{tr("nav.benefits")}</a>
          <a href="#how">{tr("nav.how")}</a>
          <a href="#features">{tr("nav.features")}</a>
          <a href="#integrations">{tr("nav.integrations")}</a>
          <a href="#cases">{tr("nav.cases")}</a>
          <a href="#faq">{tr("nav.faq")}</a>
        </nav>
        <div className="nav-cta">
          <div className="language-switcher" aria-label={tr("nav.languageLabel")}>
            <a className={lang === 'uk' ? 'is-active' : ''} href={localizedPath('uk', undefined, true)}>UA</a>
            <a className={lang === 'en' ? 'is-active' : ''} href={localizedPath('en', undefined, true)}>EN</a>
          </div>
          <a href={localizedPath(lang, '/Auth/Login')} className="nav-login">{tr("nav.login")}</a>
          <a href={localizedPath(lang, '/Register')} className="btn btn-nav">{tr("nav.createAccount")}</a>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const lang = currentLanguage();

  return (
    <footer className="foot">
      <div className="foot-inner">
        <div className="foot-brand">
          <a href={localizedPath(lang, '/')} className="logo">
            <img src={BRAND_ASSET_URLS.horizontal} alt="AI Bot" className="logo-img" />
          </a>
          <p>{tr("footer.tagline")}</p>
        </div>
        <div className="foot-cols">
          <div>
            <h5>{tr("footer.colProduct")}</h5>
            <a href="#features">{tr("footer.productFeatures")}</a>
            <a href="#integrations">{tr("footer.productIntegrations")}</a>
            <a href="#how">{tr("footer.productHow")}</a>
          </div>
          <div>
            <h5>{tr("footer.colClinics")}</h5>
            <a href="#cases">{tr("footer.clinicsCases")}</a>
            <a href={localizedPath(lang, '/Register')}>{tr("footer.clinicsRegister")}</a>
            <a href="#faq">{tr("footer.clinicsFaq")}</a>
          </div>
          <div>
            <h5>{tr("footer.colCompany")}</h5>
            <a href={localizedPath(lang, '/')}>{tr("footer.companyAbout")}</a>
            <a href={localizedPath(lang, '/')}>{tr("footer.companyBlog")}</a>
            <a href={localizedPath(lang, '/')}>{tr("footer.companyContacts")}</a>
          </div>
          <div>
            <h5>{tr("footer.colLegal")}</h5>
            <a href={localizedPath(lang, '/Privacy')}>{tr("footer.legalPrivacy")}</a>
            <a href={localizedPath(lang, '/Terms')}>{tr("footer.legalTerms")}</a>
            <a href={localizedPath(lang, '/')}>{tr("footer.legalSecurity")}</a>
          </div>
        </div>
      </div>
      <div className="foot-bottom">
        <span>{tr("footer.copyright")}</span>
        <span>{tr("footer.disclaimer")}</span>
      </div>
    </footer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// APP
// ════════════════════════════════════════════════════════════════════════════
function App() {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const t = THEMES[tweaks.theme] || THEMES.teal;

  // Apply theme variables on the root
  React.useEffect(() => {
    const r = document.documentElement.style;
    Object.entries(t).forEach(([k, v]) => r.setProperty(`--${k}`, v));
  }, [tweaks.theme]);

  return (
    <>
      <Header/>
      <main>
        <Hero t={t}/>
        <SafetyBand/>
        <Benefits/>
        <HowItWorks t={t}/>
        <Features t={t}/>
        <Integrations t={t}/>
        <UseCases t={t}/>
        <FAQ/>
        <CTA t={t}/>
      </main>
      <Footer/>
      <TweaksPanel title={tr("tweaks.title")}>
        <TweakSection label={tr("tweaks.theme")}/>
        <TweakRadio
          label={tr("tweaks.scheme")}
          value={tweaks.theme}
          options={[
            { value: 'teal', label: 'Teal' },
            { value: 'navy', label: 'Navy' },
            { value: 'warm', label: 'Warm' },
            { value: 'mono', label: 'Mono' },
          ]}
          onChange={(v) => setTweak('theme', v)}
        />
      </TweaksPanel>
    </>
  );
}

createRoot(document.getElementById("root")).render(<App />);
